import { useEffect, useState } from 'react';
import { Button, Col, Form, FormProps, Input, Row } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onGetAdmin, onUpdateAdmin } from 'modules/admins/services/admins';
import authService from 'core/services/auth';

//models
import { Admin } from 'modules/admins/models/Admin';

// components
import BackButton from 'components/backButton/backButton';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { onUpdateUser } from 'modules/users/services/users';
import { useTranslation } from 'react-i18next';

type FieldType = {
	password: string;
	password_confirmation: string;
};

function UserChangePasswordPage() {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [changePasswordForm] = Form.useForm();

	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const validatePassword = (_: any, value: string) => {
		if (!value || changePasswordForm.getFieldValue('password') === value) {
			return Promise.resolve();
		}
		return Promise.reject(new Error(t('USERS.PASSWORDS_NOT_MATCH')));
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateUserPayload = {
				password: values.password,
				password_confirmation: values.password_confirmation,
			};

			await onUpdateUser(id as string, updateUserPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('USERS.CLIENT_PASSWORD_UPDATED'));
			navigate('/users');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('USERS.CLIENT_PASSWORD_NOT_UPDATED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('USERS.CHANGE_CLIENT_PASSWORD')}</h3>
			</div>

			<Form name="changePasswordForm" form={changePasswordForm} onFinish={onSubmit}>
				<Row className="mb-3">
					{/* password input */}
					<Col span={24} md={12}>
						<h6>{t('COMMON.PASSWORD')}</h6>
						<Form.Item<FieldType>
							name="password"
							rules={[
								{ required: true, message: t('USERS.CLIENT_PASSWORD_REQUIRED') },
								{
									min: 8,
									message: t('USERS.PASSWORD_8_CHARS'),
								},
							]}>
							<Input.Password size="large" placeholder={t('USERS.ENTER_PASSWORD')} />
						</Form.Item>
					</Col>
				</Row>

				<Row className="mb-3">
					{/* confirm password input */}
					<Col span={24} md={12}>
						<h6>{t('USERS.CONFIRM_PASSWORD')}</h6>
						<Form.Item<FieldType>
							name="password_confirmation"
							rules={[{ required: true, message: t('USERS.CONFIRM_PASSWORD_REQUIRED') }, { validator: validatePassword }]}>
							<Input.Password size="large" placeholder={t('USERS.ENTER_CONFIRM_PASSWORD')} />
						</Form.Item>
					</Col>
				</Row>

				<Form.Item>
					<Button size="middle" type="primary" htmlType="submit" form="changePasswordForm" loading={isSubmitting}>
						{t('USERS.CHANGE_PASSWORD')}
					</Button>
				</Form.Item>
			</Form>
		</>
	);
}

export default UserChangePasswordPage;
